import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { Backdrop, CircularProgress } from '@mui/material'

type BackdropContextActions = {
  handleBackdropOpen: () => void
  handleBackdropClose: () => void
}

const BackdropContext = createContext({} as BackdropContextActions)

interface BackdropContextProviderProps {
  children: React.ReactNode
}

const BackdropProvider: React.FC<BackdropContextProviderProps> = ({ children }) => {
  const [openBackDrop, setOpen] = useState(false)
  // Stable identities: these functions are consumed inside useCallback/useEffect
  // dependency arrays across the app. Recreating them on every render (which
  // happens whenever the backdrop toggles) churned those deps and caused
  // fetch effects to re-fire in an infinite loop.
  const handleBackdropClose = useCallback(() => {
    setOpen(false)
  }, [])
  const handleBackdropOpen = useCallback(() => {
    setOpen(true)
  }, [])

  const value = useMemo(
    () => ({
      handleBackdropClose,
      handleBackdropOpen,
    }),
    [handleBackdropClose, handleBackdropOpen],
  )

  return (
    <BackdropContext.Provider value={value}>
      <Backdrop
        sx={{ color: '#4F0C17', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackDrop}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
      {children}
    </BackdropContext.Provider>
  )
}

const useBackdrop = (): BackdropContextActions => {
  const context = useContext(BackdropContext)

  if (!context) {
    throw new Error('useBackdrop must be used within an BackdropProvider')
  }

  return context
}

export { BackdropProvider, useBackdrop }
